import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

export default class extends Controller {
  static targets = [
    'statusIcon',
    'statusText',
    'statusSubtext',
    'progressContainer',
    'progressBar',
    'progressText',
    'pageList',
    'generateButton',
    'wipeButton',
    'messageLog'
  ]

  static values = {
    websiteId: String,
    status: String,
    pagesDataUrl: String
  }

  connect() {
    console.log('[GenerationProgress] Controller connected for website:', this.websiteIdValue)
    console.log('[GenerationProgress] Consumer connection state:', consumer.connection?.isOpen?.() ? 'open' : 'not open')

    // Subscribe to ActionCable channel
    this.subscription = consumer.subscriptions.create(
      { channel: 'WebsiteGenerationChannel', website_id: this.websiteIdValue },
      {
        connected: () => this.handleConnected(),
        disconnected: () => this.handleDisconnected(),
        rejected: () => this.handleRejected(),
        received: (data) => this.handleReceived(data)
      }
    )

    // If already generating, show the generating state
    if (this.statusValue === 'generating') {
      this.showGeneratingState()
    }
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
  }

  handleConnected() {
    console.log('[GenerationProgress] ActionCable connected')
  }

  handleDisconnected() {
    console.log('[GenerationProgress] ActionCable disconnected')
  }

  handleRejected() {
    console.error('[GenerationProgress] ActionCable subscription rejected - check authentication')
    this.addLogMessage('WebSocket connection rejected - please refresh the page', 'error')
  }

  handleReceived(data) {
    console.log('[GenerationProgress] Received:', data)

    switch (data.event) {
      case 'generating_site_components':
        this.updateStatus('generating', 'Generating Site Components...', data.message)
        this.addLogMessage(data.message, 'info')
        break

      case 'site_components_generated':
      case 'site_components_fallback':
        this.addLogMessage(data.message, 'success')
        break

      case 'page_generating':
        this.updateStatus('generating', `Generating ${data.page_type}...`, data.message)
        this.updateProgress(data.progress, data.current_page, data.total_pages)
        this.updatePageStatus(data.page_type, 'generating')
        this.addLogMessage(data.message, 'info')
        break

      case 'page_completed':
        this.updateProgress(data.progress, data.completed_pages, data.total_pages)
        this.updatePageStatus(data.page_type, 'completed')
        this.addLogMessage(data.message, 'success')
        break

      case 'page_failed':
        this.updatePageStatus(data.page_type, 'failed')
        this.addLogMessage(`${data.message}: ${data.error}`, 'error')
        break

      case 'generation_completed':
        this.updateStatus('completed', 'Generation Complete!', data.message)
        this.updateProgress(100, data.total_pages, data.total_pages)
        this.addLogMessage(data.message, 'success')
        // Refresh page thumbnails without full reload
        setTimeout(() => this.refreshPageThumbnails(), 1500)
        break

      case 'generation_failed':
        this.updateStatus('failed', 'Generation Failed', data.error)
        this.addLogMessage(`${data.message}: ${data.error}`, 'error')
        this.enableGenerateButton()
        break
    }
  }

  // Called when user clicks Generate button
  async startGeneration(event) {
    console.log('[GenerationProgress] startGeneration clicked!')
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.url

    console.log('[GenerationProgress] Button:', button)
    console.log('[GenerationProgress] URL:', url)

    if (!url) {
      console.error('[GenerationProgress] No URL found on button!')
      alert('Error: No generation URL found. Please refresh the page.')
      return
    }

    // Show immediate feedback
    this.showGeneratingState()
    this.addLogMessage('Starting website generation...', 'info')

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    console.log('[GenerationProgress] CSRF Token present:', !!csrfToken)

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      })

      console.log('[GenerationProgress] Response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        console.log('[GenerationProgress] Response data:', data)
        this.addLogMessage('Generation job queued successfully', 'success')
      } else {
        const text = await response.text()
        console.error('[GenerationProgress] Error response:', text)
        this.addLogMessage(`Failed to start generation: ${text}`, 'error')
        this.enableGenerateButton()
      }
    } catch (error) {
      console.error('[GenerationProgress] Failed to start generation:', error)
      this.addLogMessage(`Error: ${error.message}`, 'error')
      this.enableGenerateButton()
    }
  }

  // Called when user clicks Wipe & Regenerate button (dev only)
  async wipeAndRegenerate(event) {
    console.log('[GenerationProgress] wipeAndRegenerate clicked!')
    event.preventDefault()

    const button = event.currentTarget
    const url = button.dataset.url

    if (!url) {
      console.error('[GenerationProgress] No URL found on wipe button!')
      this.showToast('Error: No wipe URL found. Please refresh the page.', 'error')
      return
    }

    // Confirm with user
    if (!confirm('DEV: This will DELETE all pages, blocks, and logs, then regenerate from scratch. Continue?')) {
      return
    }

    // Show wiping state on button
    this.showWipingState(button)
    this.addLogMessage('Wiping all pages and starting fresh regeneration...', 'info')

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json'
        }
      })

      console.log('[GenerationProgress] Wipe response status:', response.status)

      if (response.ok) {
        const data = await response.json()
        console.log('[GenerationProgress] Wipe response data:', data)
        this.addLogMessage('Website wiped successfully! Starting regeneration...', 'success')
        this.showToast('Website wiped! Generation starting...', 'success')

        // Transition to generating state
        this.showGeneratingState()

        // Clear the page list visually (pages were deleted)
        this.clearPageList()
      } else {
        let errorMsg = 'Failed to wipe website'
        try {
          const data = await response.json()
          errorMsg = data.error || errorMsg
        } catch (e) {
          errorMsg = await response.text() || errorMsg
        }
        console.error('[GenerationProgress] Wipe error:', errorMsg)
        this.addLogMessage(`Wipe failed: ${errorMsg}`, 'error')
        this.showToast(`Wipe failed: ${errorMsg}`, 'error')
        this.resetWipeButton(button)
      }
    } catch (error) {
      console.error('[GenerationProgress] Failed to wipe:', error)
      this.addLogMessage(`Error: ${error.message}`, 'error')
      this.showToast(`Error: ${error.message}`, 'error')
      this.resetWipeButton(button)
    }
  }

  showWipingState(button) {
    if (button) {
      button.disabled = true
      button.innerHTML = `
        <svg class="w-4 h-4 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Wiping...
      `
    }
  }

  resetWipeButton(button) {
    if (button) {
      button.disabled = false
      button.innerHTML = `
        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
        </svg>
        Wipe & Regen
      `
    }
  }

  clearPageList() {
    // Update the page list to show empty state
    if (this.hasPageListTarget) {
      this.pageListTarget.innerHTML = ''
    }
  }

  showToast(message, type = 'info') {
    // Create a simple toast notification
    const toast = document.createElement('div')
    const bgColor = type === 'error' ? 'var(--color-error-100)' :
      type === 'success' ? 'var(--color-success-100)' :
        'var(--bg-surface)'
    const textColor = type === 'error' ? 'var(--color-error-700)' :
      type === 'success' ? 'var(--color-success-700)' :
        'var(--text-primary)'
    const borderColor = type === 'error' ? 'var(--color-error-300)' :
      type === 'success' ? 'var(--color-success-300)' :
        'var(--border-muted)'

    toast.className = 'fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg border transition-all duration-300 transform translate-x-full'
    toast.style.cssText = `background: ${bgColor}; color: ${textColor}; border-color: ${borderColor}; max-width: 400px;`
    toast.innerHTML = `
      <div class="flex items-center gap-2">
        ${type === 'success' ? '<svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>' : ''}
        ${type === 'error' ? '<svg class="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/></svg>' : ''}
        <span class="text-sm font-medium">${message}</span>
      </div>
    `

    document.body.appendChild(toast)

    // Animate in
    requestAnimationFrame(() => {
      toast.classList.remove('translate-x-full')
      toast.classList.add('translate-x-0')
    })

    // Auto-dismiss after 4 seconds
    setTimeout(() => {
      toast.classList.remove('translate-x-0')
      toast.classList.add('translate-x-full')
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }

  showGeneratingState() {
    // Update status display
    if (this.hasStatusIconTarget) {
      this.statusIconTarget.innerHTML = this.spinnerIcon()
      this.statusIconTarget.className = 'w-10 h-10 rounded-full flex items-center justify-center'
      this.statusIconTarget.style.backgroundColor = 'var(--color-primary-100)'
    }

    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = 'Starting Generation...'
    }

    if (this.hasStatusSubtextTarget) {
      this.statusSubtextTarget.textContent = 'Preparing to generate your website'
    }

    // Show progress container
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove('hidden')
    }

    // Disable generate button
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = true
      this.generateButtonTarget.innerHTML = `
        <svg class="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Generating...
      `
    }

    // Show message log
    if (this.hasMessageLogTarget) {
      this.messageLogTarget.classList.remove('hidden')
    }
  }

  updateStatus(status, text, subtext) {
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = text
    }

    if (this.hasStatusSubtextTarget) {
      this.statusSubtextTarget.textContent = subtext || ''
    }

    if (this.hasStatusIconTarget) {
      if (status === 'generating') {
        this.statusIconTarget.innerHTML = this.spinnerIcon()
        this.statusIconTarget.style.backgroundColor = 'var(--color-primary-100)'
      } else if (status === 'completed') {
        this.statusIconTarget.innerHTML = this.checkIcon()
        this.statusIconTarget.style.backgroundColor = 'var(--color-success-100)'
      } else if (status === 'failed') {
        this.statusIconTarget.innerHTML = this.errorIcon()
        this.statusIconTarget.style.backgroundColor = 'var(--color-error-100)'
      }
    }
  }

  updateProgress(percent, current, total) {
    // Cap progress at 100%
    const cappedPercent = Math.min(percent, 100)
    const displayCurrent = Math.min(current, total)

    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove('hidden')
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${cappedPercent}%`
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${cappedPercent}%`
    }

    if (this.hasStatusSubtextTarget && displayCurrent && total) {
      this.statusSubtextTarget.textContent = `${displayCurrent}/${total} pages completed`
    }
  }

  updatePageStatus(pageType, status) {
    const pageRow = document.querySelector(`[data-page-type="${pageType}"]`)
    if (!pageRow) return

    const iconContainer = pageRow.querySelector('[data-page-icon]')
    if (!iconContainer) return

    if (status === 'generating') {
      iconContainer.innerHTML = this.smallSpinnerIcon()
    } else if (status === 'completed') {
      iconContainer.innerHTML = this.smallCheckIcon()
    } else if (status === 'failed') {
      iconContainer.innerHTML = this.smallErrorIcon()
    }
  }

  addLogMessage(message, type = 'info') {
    if (!this.hasMessageLogTarget) return

    const logContainer = this.messageLogTarget.querySelector('[data-log-messages]')
    if (!logContainer) return

    const timestamp = new Date().toLocaleTimeString()
    const colorClass = type === 'error' ? 'color: var(--color-error-600)' :
      type === 'success' ? 'color: var(--color-success-600)' :
        'color: var(--text-secondary)'

    const entry = document.createElement('div')
    entry.className = 'text-sm py-1 flex gap-2'
    entry.innerHTML = `
      <span style="color: var(--text-tertiary); font-size: 0.75rem;">${timestamp}</span>
      <span style="${colorClass}">${message}</span>
    `

    logContainer.appendChild(entry)
    logContainer.scrollTop = logContainer.scrollHeight
  }

  enableGenerateButton() {
    if (this.hasGenerateButtonTarget) {
      this.generateButtonTarget.disabled = false
      this.generateButtonTarget.innerHTML = `
        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
        </svg>
        Retry Generation
      `
    }
  }

  // Refresh page thumbnails without full page reload
  async refreshPageThumbnails() {
    if (!this.hasPagesDataUrlValue) {
      console.log('[GenerationProgress] No pages data URL, falling back to reload')
      window.location.reload()
      return
    }

    try {
      this.addLogMessage('Updating page previews...', 'info')

      const response = await fetch(this.pagesDataUrlValue, {
        headers: { 'Accept': 'application/json' }
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch pages data: ${response.status}`)
      }

      const data = await response.json()

      // Update each page thumbnail
      data.pages.forEach((page) => {
        this.updatePageThumbnail(page)
      })

      this.addLogMessage('Page previews updated!', 'success')
      this.showToast('Generation complete! Previews updated.', 'success')

      // Re-enable the generate button for potential re-generation
      this.enableGenerateButton()
    } catch (error) {
      console.error('[GenerationProgress] Failed to refresh thumbnails:', error)
      this.addLogMessage('Could not update previews, reloading page...', 'info')
      // Fallback to reload if fetch fails
      setTimeout(() => window.location.reload(), 500)
    }
  }

  updatePageThumbnail(pageData) {
    // Find the thumbnail container by page ID
    const container = document.querySelector(`[data-thumbnail-page-id="${pageData.id}"]`)
    if (!container) {
      console.log('[GenerationProgress] Thumbnail container not found for page:', pageData.id)
      return
    }

    // Update thumbnail status attribute
    container.dataset.thumbnailStatus = pageData.thumbnail_status || 'pending'

    if (pageData.thumbnail_url && pageData.thumbnail_status === 'ready') {
      // Replace container content with the ready state (image)
      container.innerHTML = `
        <div class="absolute inset-0 skeleton-shimmer" data-image-loader-target="skeleton">
          <div class="w-full h-full skeleton-gradient animate-shimmer"></div>
        </div>
        <img src="${pageData.thumbnail_url}"
             class="w-full h-full object-cover object-top opacity-0 transition-opacity duration-300"
             loading="lazy"
             alt="Preview of ${pageData.title}"
             data-image-loader-target="image"
             onload="this.classList.remove('opacity-0'); this.classList.add('opacity-100'); this.previousElementSibling?.remove();" />
      `
    } else if (pageData.thumbnail_status === 'capturing') {
      // Show capturing state
      container.innerHTML = `
        <div class="flex items-center justify-center w-full h-full">
          <div class="flex flex-col items-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-current mb-2"
                 style="color: var(--text-muted);"></div>
            <span class="text-xs" style="color: var(--text-muted);">Capturing...</span>
          </div>
        </div>
      `
    }
    // For pending/failed states, the existing content can stay

    // Also update the page card status icon
    const pageCard = document.querySelector(`[data-page-id="${pageData.id}"]`)
    if (pageCard) {
      const iconContainer = pageCard.querySelector('[data-page-icon]')
      if (iconContainer) {
        if (pageData.generation_status === 'completed' && pageData.used_fallback) {
          iconContainer.innerHTML = this.fallbackIcon()
        } else if (pageData.generation_status === 'completed') {
          iconContainer.innerHTML = this.completedIcon()
        }
      }
    }
  }

  fallbackIcon() {
    return `
      <div class="w-5 h-5 rounded-full flex items-center justify-center" style="background: var(--color-warning-100);">
        <svg class="w-3 h-3" style="color: var(--color-warning-600);" fill="currentColor" viewBox="0 0 20 20" title="Used template content">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
      </div>
    `
  }

  completedIcon() {
    return `
      <div class="w-5 h-5 rounded-full flex items-center justify-center" style="background: var(--color-success-100);">
        <svg class="w-3 h-3" style="color: var(--color-success-600);" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
        </svg>
      </div>
    `
  }

  // Icon helpers
  spinnerIcon() {
    return `
      <svg class="w-5 h-5 animate-spin" style="color: var(--color-primary-600);" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
    `
  }

  checkIcon() {
    return `
      <svg class="w-5 h-5" style="color: var(--color-success-600);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
      </svg>
    `
  }

  errorIcon() {
    return `
      <svg class="w-5 h-5" style="color: var(--color-error-600);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    `
  }

  smallSpinnerIcon() {
    return `
      <svg class="w-4 h-4 animate-spin" style="color: var(--color-primary-500);" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
      </svg>
    `
  }

  smallCheckIcon() {
    return `
      <svg class="w-4 h-4" style="color: var(--color-success-500);" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
      </svg>
    `
  }

  smallErrorIcon() {
    return `
      <svg class="w-4 h-4" style="color: var(--color-error-500);" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
      </svg>
    `
  }
}
